import React from 'react';
import './liveSummaryFocus.css';

export type DecisionBriefItem = { code: string; text: string; authority: string };
export type DecisionBriefV1 = {
  version: 'LIVE_SUMMARY_DECISION_BRIEF_V1';
  status: 'OBSERVED' | 'STALE' | 'NO_DATA';
  observedAt: string;
  regimeLabel: string;
  summary: string;
  certainty: string;
  horizon: string;
  optionWhaleFlow: string;
  eventCausality: string;
  observations: DecisionBriefItem[];
  riskIndicators: DecisionBriefItem[];
  nextChecks: string[];
  unverified: string[];
};
export type OptionResearch = {
  version: 'PI_CFI_OPTION_PAIR_RESEARCH_V1';
  status: string;
  observedAt?: string;
  expiryRuleOnly?: string;
  daysToExpiryRuleOnly?: number;
  marketDirection?: string;
  optionToStockAttribution?: string;
  volumeDeltaAvailable?: boolean;
  isLive?: boolean;
};
type Props = { brief?: DecisionBriefV1 | null; optionResearch?: OptionResearch | null };

function displayTime(value?: string): string {
  if (!value) return '기준 시각 확인 중';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '기준 시각 확인 중';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date);
}

function simpleLine(value?: string, maxLength = 85): string {
  const cleaned = (value || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength - 1).trimEnd()}…` : cleaned;
}

/** Primary judgment UI. A non-OBSERVED brief never becomes a directional recommendation. */
export default function LiveSummaryDecisionBriefCard({ brief, optionResearch }: Props): React.ReactElement {
  const valid = brief?.version === 'LIVE_SUMMARY_DECISION_BRIEF_V1';
  const observed = valid && brief?.status === 'OBSERVED';
  const supporting = observed ? simpleLine(brief?.observations?.[0]?.text) : '';
  const turning = observed
    ? simpleLine(brief?.riskIndicators?.[0]?.text || brief?.nextChecks?.[0])
    : '';
  const title = observed ? simpleLine(brief?.regimeLabel, 46) || '시장 상태 확인 중' : '방향 판단 보류';
  const summary = observed ? simpleLine(brief?.summary, 115) : '방향을 판단할 근거가 충분하지 않습니다.';
  return (
    <section className="pi-direction-hero" aria-label="현재 시장 판단">
      <div className="pi-direction-hero__eyebrow">
        <span className="pi-direction-hero__dot" aria-hidden="true" />
        <span>시장 판단</span>
        <span className="pi-direction-hero__status">{observed ? '근거 확인' : brief?.status === 'STALE' ? '이전 기준' : '판단 대기'}</span>
      </div>
      <h2>{title}</h2>
      <p className="pi-direction-hero__summary">{summary}</p>
      <div className="pi-direction-hero__signals">
        <div>
          <span>방향을 뒷받침하는 근거</span>
          <strong>{supporting || '아직 확인된 근거 없음'}</strong>
        </div>
        <div>
          <span>다시 판단할 조건</span>
          <strong>{turning || '새로운 시장·수급 반응 확인'}</strong>
        </div>
      </div>
      {observed && Boolean((brief?.observations?.length || 0) + (brief?.riskIndicators?.length || 0) + (brief?.nextChecks?.length || 0)) && (
        <details className="pi-direction-hero__details">
          <summary>근거 전체 보기 <span aria-hidden="true">⌄</span></summary>
          <div>
            {Boolean(brief?.observations?.length) && <section><h3>확인된 근거</h3><ul>{brief!.observations.slice(0, 4).map((item, i) => <li key={`obs-${item.code}-${i}`}>{item.text}</li>)}</ul></section>}
            {Boolean(brief?.riskIndicators?.length) && <section><h3>위험 신호</h3><ul>{brief!.riskIndicators.slice(0, 4).map((item, i) => <li key={`risk-${item.code}-${i}`}>{item.text}</li>)}</ul></section>}
            {Boolean(brief?.nextChecks?.length) && <section><h3>다시 판단할 조건</h3><ul>{brief!.nextChecks.slice(0, 4).map((item, i) => <li key={`check-${i}`}>{item}</li>)}</ul></section>}
          </div>
        </details>
      )}
      {optionResearch?.version === 'PI_CFI_OPTION_PAIR_RESEARCH_V1' && (
        <aside className="pi-option-research-status" aria-label="옵션 연구 관측 상태">
          <strong>옵션 관측 · 연구용</strong>
          <p>{optionResearch.status === 'PASSIVE_PAIR_OBSERVED_NOT_DIRECTIONAL'
            ? '풋·콜 계약쌍의 거래 활동은 관측됐지만 시장 방향과 매수 주체는 확인되지 않았습니다.'
            : optionResearch.status === 'RESEARCH_DISABLED'
              ? '옵션 연구 기능이 아직 활성화되지 않았습니다.'
              : optionResearch.status === 'HISTORICAL_SAMPLE_ONLY' || optionResearch.status === 'CONTRACT_EXPIRED'
                ? '과거 계약 관측입니다. 현재 시장 방향 판단에 사용하지 않습니다.'
                : '검증된 옵션 관측 데이터가 없어 방향성 판단에서 제외합니다.'}</p>
          {optionResearch.status === 'PASSIVE_PAIR_OBSERVED_NOT_DIRECTIONAL' && optionResearch.observedAt && (
            <small>관측 시각: {displayTime(optionResearch.observedAt)} · 단일 행사가 연구 표본</small>
          )}
        </aside>
      )}
      <footer><span>{observed ? '관측 기준' : '자료 기준'} · {displayTime(valid ? brief?.observedAt : undefined)}</span><span>근거 없는 방향 예측 제외</span></footer>
    </section>
  );
}
